import type { Peticion, MensajeWorker, Progreso, EstadoInicial, FeatureCollectionSimple, FeatureCollectionLineas } from './protocolo';
import type { Criterios, Resultado, Ficha, Resumen, CandidatoCompleto, TablaExportable } from './consultas';
import type { ResultadoHidro } from '../ign/cargadorHidro';
import type { Candidato, BusquedaGuardada, Fila, Inmueble, Parcela, TeselaCargada } from './tipos';
import type { Tesela } from '../geo/teselado';
import type { Bbox, Punto } from '../geo/geometria';
import type { CapaCatastro } from '../catastro/wfs';
import type { ResultadoCarga } from '../catastro/cargador';
import type { GeometriasUtm } from '../export/exportarDxf';

/**
 * Proxy del hilo principal hacia el worker de la base de datos. Cada metodo es una peticion
 * con respuesta; las cargas aceptan un callback de progreso.
 */
export class ClienteDb {
  private readonly worker: Worker;
  private siguienteId = 1;
  private readonly pendientes = new Map<number, {
    resolver: (v: unknown) => void;
    rechazar: (e: Error) => void;
    onProgreso?: (p: Progreso) => void;
  }>();

  constructor() {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'cernicalo-db' });
    this.worker.onmessage = (ev: MessageEvent<MensajeWorker>) => {
      const m = ev.data;
      const p = this.pendientes.get(m.id);
      if (!p) return;
      if ('progreso' in m) { p.onProgreso?.(m.progreso); return; }
      this.pendientes.delete(m.id);
      if (m.ok) p.resolver(m.resultado); else p.rechazar(new Error(m.error));
    };
    this.worker.onerror = (ev) => {
      const error = new Error(`Fallo en el worker de la base de datos: ${ev.message}`);
      for (const p of this.pendientes.values()) p.rechazar(error);
      this.pendientes.clear();
    };
  }

  private temporizadorPausa: ReturnType<typeof setTimeout> | null = null;

  private llamar<T>(peticion: Peticion, onProgreso?: (p: Progreso) => void): Promise<T> {
    const id = this.siguienteId++;
    if (this.temporizadorPausa) { clearTimeout(this.temporizadorPausa); this.temporizadorPausa = null; }
    return new Promise<T>((resolver, rechazar) => {
      this.pendientes.set(id, {
        resolver: (v) => { this.programarPausa(); resolver(v as T); },
        rechazar: (e) => { this.programarPausa(); rechazar(e); },
        onProgreso,
      });
      this.worker.postMessage({ id, peticion });
    });
  }

  /** Tras unos segundos sin peticiones se liberan los ficheros de OPFS (ver worker.pausar). */
  private programarPausa(): void {
    if (this.pendientes.size > 0) return;
    if (this.temporizadorPausa) clearTimeout(this.temporizadorPausa);
    this.temporizadorPausa = setTimeout(() => this.pausar(), 4000);
  }

  /** Libera OPFS de inmediato (se llama al descargar la pagina); no espera respuesta. */
  pausar(): void {
    if (this.pendientes.size > 0) return;
    this.worker.postMessage({ id: 0, peticion: { tipo: 'pausar' } satisfies Peticion });
  }

  /** Arranque con tiempo limite: un worker que no responde se convierte en un error visible. */
  iniciar(timeoutMs = 45_000): Promise<EstadoInicial> {
    return Promise.race([
      this.llamar<EstadoInicial>({ tipo: 'iniciar' }),
      new Promise<EstadoInicial>((_, rechazar) => setTimeout(() => rechazar(new Error(`el worker no respondió en ${timeoutMs / 1000} s`)), timeoutMs)),
    ]);
  }
  resumen(): Promise<Resumen> { return this.llamar({ tipo: 'resumen' }); }
  cargarZona(teselas: Tesela[], capas: CapaCatastro[], dias: number, concurrencia: number, onProgreso?: (p: Progreso) => void): Promise<ResultadoCarga> {
    return this.llamar({ tipo: 'cargarZona', teselas, capas, dias, concurrencia }, onProgreso);
  }
  cancelarCarga(): Promise<boolean> { return this.llamar({ tipo: 'cancelarCarga' }); }
  cargarPartes(bbox: Bbox, epsg: number, dias: number, onProgreso?: (p: Progreso) => void): Promise<ResultadoCarga> {
    return this.llamar({ tipo: 'cargarPartes', bbox, epsg, dias }, onProgreso);
  }
  cargarHidrografia(bbox: Bbox, epsg: number, dias: number, onProgreso?: (p: Progreso) => void): Promise<ResultadoHidro> {
    return this.llamar({ tipo: 'cargarHidrografia', bbox, epsg, dias }, onProgreso);
  }
  aguaGeoJson(bbox: Bbox): Promise<FeatureCollectionLineas> { return this.llamar({ tipo: 'aguaGeoJson', bbox }); }
  buscar(criterios: Criterios): Promise<Resultado[]> { return this.llamar({ tipo: 'buscar', criterios }); }
  ficha(rc: string): Promise<Ficha | null> { return this.llamar({ tipo: 'ficha', rc }); }
  parcelaEnPunto(punto: Punto): Promise<Parcela | null> { return this.llamar({ tipo: 'parcelaEnPunto', punto }); }
  parcelasGeoJson(bbox: Bbox): Promise<FeatureCollectionSimple> { return this.llamar({ tipo: 'parcelasGeoJson', bbox }); }
  partesGeoJson(bbox: Bbox): Promise<FeatureCollectionSimple> { return this.llamar({ tipo: 'partesGeoJson', bbox }); }
  geometriasUtm(bbox: Bbox): Promise<GeometriasUtm> { return this.llamar({ tipo: 'geometriasUtm', bbox }); }
  aplicarAlturas(alturas: { rc: string; aleroM: number | null; cumbreraM: number | null; huellaCorregida: string | null; desviacionM: number | null }[]): Promise<number> {
    return this.llamar({ tipo: 'aplicarAlturas', alturas });
  }
  inmuebles(rc: string, forzar = false): Promise<Inmueble[]> { return this.llamar({ tipo: 'inmuebles', rc, forzar }); }
  guardarCandidato(candidato: Candidato): Promise<boolean> { return this.llamar({ tipo: 'guardarCandidato', candidato }); }
  borrarCandidato(rc: string): Promise<boolean> { return this.llamar({ tipo: 'borrarCandidato', rc }); }
  candidatos(): Promise<CandidatoCompleto[]> { return this.llamar({ tipo: 'candidatos' }); }
  guardarBusqueda(busqueda: BusquedaGuardada): Promise<boolean> { return this.llamar({ tipo: 'guardarBusqueda', busqueda }); }
  borrarBusqueda(id: string): Promise<boolean> { return this.llamar({ tipo: 'borrarBusqueda', id }); }
  busquedas(): Promise<BusquedaGuardada[]> { return this.llamar({ tipo: 'busquedas' }); }
  consultaLibre(sql: string): Promise<Fila[]> { return this.llamar({ tipo: 'consultaLibre', sql }); }
  exportarTabla(tabla: TablaExportable): Promise<Fila[]> { return this.llamar({ tipo: 'exportarTabla', tabla }); }
  teselas(): Promise<TeselaCargada[]> { return this.llamar({ tipo: 'teselas' }); }
  borrarTeselas(ids: string[]): Promise<boolean> { return this.llamar({ tipo: 'borrarTeselas', ids }); }
  vaciarCatastro(): Promise<boolean> { return this.llamar({ tipo: 'vaciarCatastro' }); }
  vaciarTodo(): Promise<boolean> { return this.llamar({ tipo: 'vaciarTodo' }); }
}
